import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocation as useUserLocation } from "@/lib/useLocation";
import AISageTip from "@/components/AISageTip";
import CompanionsList from "@/components/CompanionsList";
import PhotoGallery from "@/components/PhotoGallery";
import HarvestCalendar from "@/components/HarvestCalendar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sun, Droplet, Sprout, Clock, Ruler, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PlantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { location } = useUserLocation();
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [harvestMonths, setHarvestMonths] = useState([]);

  const load = () => {
    api.get(`/garden/${id}`).then((r) => {
      setPlant(r.data);
      api.get(`/harvest-months/${encodeURIComponent(r.data.name)}?category=${encodeURIComponent(r.data.category)}`)
        .then((m) => setHarvestMonths(m.data.months || []));
    }).catch(() => setPlant(null)).finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    if (!plant) return;
    await api.delete(`/garden/${plant.id}`);
    toast.message(`${plant.name} removed.`);
    navigate("/garden");
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!plant) return (
    <div className="space-y-4">
      <p className="text-sm">Plant not found.</p>
      <Link to="/garden"><Button variant="outline">Back to garden</Button></Link>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="plant-detail">
      <Link to="/garden" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-garden">
        <ArrowLeft className="w-4 h-4" /> Back to garden
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <PhotoGallery plant={plant} onChange={load} />
          <div className="px-1">
            <div className="label-overline">{plant.category}</div>
            <h1 className="font-serif text-4xl font-light mt-1">{plant.name}</h1>
          </div>
        </div>

        <div className="lg:col-span-2 sage-card p-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Detail icon={Sun} label="Sun" value={plant.sun} />
            <Detail icon={Droplet} label="Water" value={plant.water} />
            <Detail icon={Sprout} label="Season" value={plant.season} />
            <Detail icon={Clock} label="Days to harvest" value={`${plant.days_to_harvest}`} />
            <Detail icon={Ruler} label="Spacing" value={`${plant.spacing_in}"`} />
          </div>
          {plant.description && (
            <>
              <div className="sage-divider my-5" />
              <p className="text-sm text-foreground/80 leading-relaxed">{plant.description}</p>
            </>
          )}
          {plant.notes && (
            <>
              <div className="sage-divider my-5" />
              <div className="label-overline mb-2">My notes</div>
              <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{plant.notes}</p>
            </>
          )}
          <div className="mt-6 flex justify-end">
            <Button variant="ghost" onClick={remove} className="text-destructive/80 hover:text-destructive gap-1" data-testid="remove-detail-btn">
              <Trash2 className="w-4 h-4" /> Remove from garden
            </Button>
          </div>
        </div>
      </div>

      <HarvestCalendar focusMonths={harvestMonths} focusName={plant.name} />

      <CompanionsList name={plant.name} category={plant.category} />

      <AISageTip scope="plant" plantId={plant.id} location={location} title={`Sage's counsel for ${plant.name}`} />
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 mt-0.5 text-accent" strokeWidth={1.5} />
      <div>
        <div className="label-overline">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
